import { Tag } from '@/components/ui/tag';
import { MOTION } from '@/utils';
import { withBasePath } from '@/utils/basePath';
import { LOGOS } from '@/utils/data/logo';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import Image from 'next/image';
import { useRef } from 'react';

const ORBIT_DURATION_S = 45;
const ORBIT_SPEED = (Math.PI * 2) / (ORBIT_DURATION_S * 60);
const ORBIT_BURST_FACTOR = 6;
const ORBIT_ACCEL_DURATION = 0.6;
const ORBIT_DECEL_DURATION = 2.0;
const ORBIT_LEAVE_DECEL = 0.4;
const ORBIT_START_DELAY = 0.85;
const RADIUS_MOBILE = 120;

const baseAngle = (i: number, count: number) => (Math.PI * 2 * i) / count - Math.PI / 2;

function positionLogos(logos: HTMLElement[], angle: number, radius: number) {
  const count = logos.length;
  logos.forEach((logo, i) => {
    gsap.set(logo, {
      x: Math.cos(baseAngle(i, count) + angle) * radius,
      y: Math.sin(baseAngle(i, count) + angle) * radius,
      force3D: true, // promote GPU layer
    });
  });
}

export default function Adaptative() {
  const refs = {
    section: useRef<HTMLElement>(null),
    tag: useRef(null),
    title: useRef(null),
    text: useRef(null),
    logos: useRef<HTMLDivElement>(null),
    outerRing: useRef(null),
    innerRing: useRef(null),
    center: useRef<HTMLDivElement>(null),
  };

  useGSAP(() => {
    const { section, tag, title, text, logos, outerRing, innerRing, center } = refs;
    if (
      !section.current ||
      !logos.current ||
      !outerRing.current ||
      !innerRing.current ||
      !center.current
    )
      return;

    const logoEls = gsap.utils.toArray<HTMLElement>('[data-orbit-item]', logos.current);

    const splitTitle = new SplitText(title.current, { type: 'lines', mask: 'lines' });
    const splitText = new SplitText(text.current, { type: 'lines', mask: 'lines' });

    const orbit = { vel: 0, angle: 0 };
    let velTween: gsap.core.Tween | null = null;
    let orbitDelay: gsap.core.Tween | null = null;
    let tickerRunning = false;

    // Rayon dérivé de la taille du container — r=200 sur viewBox=500 → ratio 0.4
    const getRadius = () => (logos.current?.offsetWidth ?? RADIUS_MOBILE * 2) * 0.4;
    let radius = getRadius();
    const ro = new ResizeObserver(() => {
      radius = getRadius();
      if (!tickerRunning) positionLogos(logoEls, orbit.angle, radius);
    });
    ro.observe(logos.current);

    const tick = () => {
      orbit.angle += orbit.vel;
      positionLogos(logoEls, orbit.angle, radius);
    };

    const startTicker = () => {
      if (tickerRunning) return;
      orbit.angle = 0;
      positionLogos(logoEls, 0, radius);
      gsap.ticker.add(tick);
      tickerRunning = true;
    };

    const stopTicker = () => {
      if (!tickerRunning) return;
      gsap.ticker.remove(tick);
      tickerRunning = false;
      orbit.vel = 0;
    };

    const burstAndDecel = () => {
      velTween?.kill();
      orbit.vel = 0;
      velTween = gsap.to(orbit, {
        vel: ORBIT_SPEED * ORBIT_BURST_FACTOR,
        duration: ORBIT_ACCEL_DURATION,
        ease: 'power2.in',
        onComplete: () => {
          velTween = gsap.to(orbit, {
            vel: ORBIT_SPEED,
            duration: ORBIT_DECEL_DURATION,
            ease: 'power3.out',
          });
        },
      });
    };

    const scheduleOrbit = () => {
      orbitDelay?.kill();
      orbitDelay = gsap.delayedCall(ORBIT_START_DELAY, burstAndDecel);
    };

    const coastToStop = () => {
      orbitDelay?.kill();
      velTween?.kill();
      velTween = gsap.to(orbit, {
        vel: 0,
        duration: ORBIT_LEAVE_DECEL,
        ease: 'power2.out',
      });
    };

    gsap.set(tag.current, { autoAlpha: 0, y: 8 });
    gsap.set(logoEls, { autoAlpha: 0, scale: 0.9, xPercent: -50, yPercent: -50, force3D: true });
    positionLogos(logoEls, 0, radius);
    gsap.set([innerRing.current, outerRing.current], {
      strokeWidth: 0,
      autoAlpha: 0,
      scale: 0.9,
      svgOrigin: '250 250',
    });
    gsap.set(center.current, { autoAlpha: 0 });

    const tl = gsap.timeline({
      onReverseComplete: stopTicker,
      scrollTrigger: {
        trigger: section.current,
        start: 'top 60%',
        toggleActions: 'play none none reverse',
        onEnter: () => {
          startTicker();
          scheduleOrbit();
        },
        onLeaveBack: coastToStop,
      },
    });

    tl.to(tag.current, { autoAlpha: 1, y: 0, duration: MOTION.durationSm, ease: MOTION.ease })
      .from(
        splitTitle.lines,
        {
          yPercent: MOTION.yPercent,
          stagger: MOTION.staggerLines,
          duration: MOTION.durationMd,
          ease: MOTION.ease,
        },
        '<+0.15',
      )
      .from(
        splitText.lines,
        {
          yPercent: MOTION.yPercent,
          stagger: MOTION.staggerLines,
          duration: MOTION.durationMd,
          ease: MOTION.ease,
        },
        '<+0.2',
      )
      .to(center.current, { autoAlpha: 1, duration: MOTION.durationMd, ease: MOTION.ease }, '<')
      .to(
        innerRing.current,
        { strokeWidth: 1, autoAlpha: 1, scale: 1, duration: 0.9, ease: 'power3.out' },
        '<+0.1',
      )
      .to(
        outerRing.current,
        { strokeWidth: 1, autoAlpha: 1, scale: 1, duration: 1.1, ease: 'power3.out' },
        '<+0.35',
      )
      .to(
        logoEls,
        {
          autoAlpha: 1,
          scale: 1,
          duration: 1.2,
          ease: 'power2.out',
          stagger: { each: 0.05, ease: 'power2.in' },
        },
        '<+0.05',
      );

    return () => {
      ro.disconnect();
      stopTicker();
      orbitDelay?.kill();
      velTween?.kill();
      splitTitle.revert();
      splitText.revert();
    };
  });

  return (
    <section ref={refs.section} className="max-width-white-section">
      <div className="max-width-white-section-content grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-8">
        {/* Colonne texte */}
        <div className="flex flex-col gap-5 text-left">
          <Tag ref={refs.tag}>Compatibilité universelle</Tag>

          <div className="flex w-full flex-col gap-2">
            <h2 ref={refs.title} className="text-left">
              zLawyer s&apos;adapte à votre organisation
            </h2>

            <p
              ref={refs.text}
              aria-hidden="true"
              className="text-dark-blue/75 max-w-lg leading-relaxed"
            >
              Que votre cabinet soit équipé d&apos;un <span className="font-roboto-bold">NAS</span>,
              d&apos;un serveur ou encore d&apos;un <span className="font-roboto-bold">cloud</span>,
              zLawyer <span className="font-roboto-bold">s&apos;adapte</span> parfaitement à{' '}
              <span className="font-roboto-bold">votre organisation</span>. zLawyer est{' '}
              <span className="font-roboto-bold whitespace-nowrap">100% compatible</span> avec les
              technologies actuelles de stockage.
            </p>

            <p className="sr-only">
              Que votre cabinet soit équipé d&apos;un NAS, d&apos;un serveur ou encore d&apos;un
              cloud, zLawyer s&apos;adapte parfaitement à votre organisation. zLawyer est 100%
              compatible avec les technologies actuelles de stockage.
            </p>
          </div>
        </div>

        {/* Colonne orbit */}
        <div className="relative mx-auto h-[280px] w-[280px] overflow-hidden md:h-[500px] md:w-[500px]">
          {/* Cercles décoratifs */}
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full"
            fill="none"
            style={{ filter: 'drop-shadow(0 0 80px rgba(255,124,67,0.07))' }}
            viewBox="0 0 500 500"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle ref={refs.innerRing} cx="250" cy="250" r="110" stroke="rgba(255,124,67,0.5)" />
            <circle ref={refs.outerRing} cx="250" cy="250" r="200" stroke="rgba(255,124,67,0.2)" />
          </svg>

          {/* Centre : logo zLawyer */}
          <div
            ref={refs.center}
            className="border-orange/80 absolute top-1/2 left-1/2 z-20 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border shadow-lg md:h-24 md:w-24"
          >
            <Image
              alt="Logo zLawyer"
              className="h-auto w-full object-contain md:w-12"
              decoding="async"
              height={60}
              priority={false}
              src={withBasePath('/images/adaptative/zlawyer.svg')}
              width={180}
            />
          </div>

          <div ref={refs.logos} className="absolute inset-0">
            {LOGOS.map((logo) => (
              <div
                key={logo.src}
                className="absolute top-1/2 left-1/2 z-10 h-12 w-12 will-change-transform md:h-15 md:w-15"
                data-orbit-item
              >
                <Image
                  alt={logo.alt}
                  className="h-full w-full object-contain"
                  decoding="async"
                  height={logo.height}
                  loading="lazy"
                  src={logo.src}
                  width={logo.width}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
